<template>
  <v-dialog
    v-model="localOpen"
    max-width="560px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    @input="handleDialogChange"
  >
    <v-card class="clara-intro">
      <!-- O gradiente da Clara só aparece AQUI e no painel dela. É a exceção de
           marca já registrada nos tokens (bloco 7.1); não estenda para o app. -->
      <div class="clara-intro__hero">
        <ClaraStrands class="clara-intro__fios" />
        <ClaraLogo
          class="clara-intro__logo"
          variant="reverse"
        />
        <p class="clara-intro__tagline">{{ $t('clara.intro.tagline') }}</p>
      </div>

      <v-card-text class="clara-intro__body">
        <p class="clara-intro__lead">
          {{ $t('clara.intro.lead') }}
        </p>

        <ul class="clara-intro__list">
          <li
            v-for="item in highlights"
            :key="item.titleKey"
            class="clara-intro__item"
          >
            <span class="clara-intro__item-icon">
              <component
                :is="item.icon"
                :size="18"
              />
            </span>
            <span class="clara-intro__item-body">
              <strong class="clara-intro__item-title">{{ $t(item.titleKey) }}</strong>
              <span class="clara-intro__item-text">{{ $t(item.textKey) }}</span>
            </span>
          </li>
        </ul>
      </v-card-text>

      <v-divider />

      <v-card-actions class="clara-intro__actions">
        <v-checkbox
          v-model="dontShowToday"
          class="clara-intro__checkbox mt-0 pt-0"
          hide-details
          dense
          data-test="clara-intro-dont-show-today"
          :label="$t('clara.intro.dontShowToday')"
        />
        <v-spacer />
        <v-btn
          text
          data-test="clara-intro-later"
          @click="close"
        >
          {{ $t('clara.intro.laterBtn') }}
        </v-btn>
        <v-btn
          depressed
          class="clara-intro__cta"
          data-test="clara-intro-start"
          @click="start"
        >
          {{ $t('clara.intro.startBtn') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClaraLogo from '@/views/clara/ClaraLogo.vue';
import ClaraStrands from '@/views/clara/ClaraStrands.vue';

export default {
  name: 'ClaraIntroModal',

  components: { ClaraLogo, ClaraStrands },

  props: {
    open: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      dontShowToday: false,
      highlights: [
        {
          icon: 'PhMagnifyingGlass',
          titleKey: 'clara.intro.highlights.knowsYourOp.title',
          textKey: 'clara.intro.highlights.knowsYourOp.text'
        },
        {
          icon: 'PhCompass',
          titleKey: 'clara.intro.highlights.systemHelp.title',
          textKey: 'clara.intro.highlights.systemHelp.text'
        },
        {
          icon: 'PhShieldCheck',
          titleKey: 'clara.intro.highlights.secure.title',
          textKey: 'clara.intro.highlights.secure.text'
        }
      ]
    };
  },

  computed: {
    localOpen: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit('update:open', value);
      }
    }
  },

  watch: {
    open(newValue) {
      if (newValue) this.dontShowToday = false;
    }
  },

  methods: {
    /* A marcação de "não ver hoje" viaja junto do fechamento: quem clica no X,
       no overlay ou no ESC também está fechando, e a caixa marcada tem de valer
       do mesmo jeito. */
    emitClose(eventName) {
      this.$emit(eventName, { dontShowToday: this.dontShowToday });
      this.$emit('update:open', false);
    },

    handleDialogChange(value) {
      if (!value) this.emitClose('close');
    },

    close() {
      this.emitClose('close');
    },

    start() {
      this.emitClose('start');
    }
  }
};
</script>

<style scoped>
.clara-intro {
  overflow: hidden;
}

.clara-intro__hero {
  position: relative;
  overflow: hidden;
  background: var(--clara-gradient-surface);
  color: #fff;
  padding: var(--tc-space-7) var(--tc-space-5) var(--tc-space-6);
  text-align: center;
}

/* Os fios ficam ATRÁS e fora do fluxo; a logo e a tagline sobem com z-index
   próprio para o brilho não passar por cima do texto. */
.clara-intro__fios {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.clara-intro__logo,
.clara-intro__tagline {
  position: relative;
  z-index: 1;
}

.clara-intro__logo {
  width: min(230px, 62%);
  margin: 0 auto var(--tc-space-4);
}

.clara-intro__title {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  color: #fff;
}

.clara-intro__tagline {
  margin: var(--tc-space-2) 0 0;
  font-size: 0.95rem;
  color: var(--clara-brand-soft);
}

.clara-intro__body {
  padding: var(--tc-space-5) !important;
}

.clara-intro__lead {
  margin: 0 0 var(--tc-space-5);
  font-size: 0.95rem;
  line-height: 1.6;
}

.clara-intro__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.clara-intro__item {
  display: flex;
  align-items: flex-start;
  gap: var(--tc-space-3);
  line-height: 1.5;
}

.clara-intro__item + .clara-intro__item {
  margin-top: var(--tc-space-4);
}

.clara-intro__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: var(--tc-radius-sm);
  background: var(--clara-tint);
  color: var(--clara-text);
}

/* Titulo em cima, descricao embaixo. Em linha, o rotulo se perdia dentro do
   paragrafo e os itens liam como um bloco de texto so. */
.clara-intro__item-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.clara-intro__item-title {
  font-weight: 600;
}

.clara-intro__item-text {
  font-size: 0.9rem;
  color: var(--tc-text-2);
}

.clara-intro__actions {
  flex-wrap: wrap;
  gap: var(--tc-space-2);
  padding: var(--tc-space-3) var(--tc-space-5);
}

.clara-intro__checkbox {
  flex: 0 1 auto;
}

.clara-intro__checkbox ::v-deep label {
  font-size: 0.875rem;
}

/* O CTA carrega o gradiente da Clara: é o único botão da tela que precisa ler
   como "isto é o produto novo". */
.clara-intro__cta {
  background: var(--clara-gradient) !important;
  color: var(--clara-on-gradient) !important;
  font-weight: 600;
}
</style>
